'use client';

/**
 * API namespace `payout`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L4980-L5047).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const payout = {
  /**
   * GET /payout/:payoutId — single payout status (user-facing).
   *
   * Returns `PayoutDto` (the same shape used in admin pages —
   * `_id`, `userId`, `amount`, `fee`, `netAmount`, `network`,
   * `walletAddress`, `status`, `txHash?`, `failureReason?`,
   * `processedAt?`, `completedAt?`, `createdAt`, `updatedAt`).
   *
   * Authz: self only. The backend controller verifies
   * `payout.userId === req.user!.sub` via `payoutService.getPayout(id,
   * userId)` (which adds the userId filter to the Mongoose query).
   * 404 if payout not found.
   */
  getById(payoutId: string, signal?: AbortSignal): Promise<PayoutDto> {
    return apiFetch<PayoutDto>(
      `/payout/${encodeURIComponent(payoutId)}`,
      { signal },
    );
  },
  /**
   * POST /payout/request — request a withdrawal.
   *
   * Spec ref: SPEC-016 §3.1, SPEC-NEGOCIO §4.3 (Reglas de Retiro),
   * §9.3 (INV-SEC-03 idempotency via `clientRequestId` UUID v4 —
   * Bug #14 fix).
   *
   * Authz: JWT + `requireRecent2FA` middleware (5min Redis key —
   * the FE surfaces 403 `2FA_REQUIRED` via the global event hook in
   * `apiFetch` so the wallet page's 2FA dialog can prompt). The
   * backend also returns HTTP 503 `PAYOUTS_DISABLED` when the
   * `payout_withdraw` feature flag is off (MVP default — the FE
   * disables the "Retirar" button to prevent the request entirely).
   *
   * Returns `PayoutResult` with `payoutId`, `status='pending'`,
   * `amount`, `fee`, `netAmount`, `network`, `walletAddress`,
   * `createdAt`. The payout moves to `processing` → `completed`
   * (or `failed` → `refunded`) via the admin + withdrawal-processor
   * flow.
   *
   * R3-BATCH-6: the wallet page (`frontend/src/app/app/wallet/page.tsx`)
   * still calls `apiFetch('/payout/request', ...)` directly with a
   * TODO(QA-AUDIT) comment at L470. Switching the caller to
   * `api.payout.request(body)` is owned by Batch-4 (frontend pages)
   * — that's NOT touched here. The TODO comment in wallet/page.tsx
   * is left in place; a future Batch-4 refactor will replace the raw
   * `apiFetch` call with `await api.payout.request({ amount, network,
   * walletAddress, clientRequestId })` (same body shape, but typed).
   */
  request(body: PayoutRequestBody, signal?: AbortSignal): Promise<PayoutResult> {
    return apiFetch<PayoutResult>('/payout/request', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  },
};
